import React, { useEffect, useState } from 'react';
import { Image, Button, Stack, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { IMAGE_PATHS } from 'constants/imagePaths';
import { S3_URL } from 'constants';
import { getCreatedDifferenceInBestUnit, unitNumber } from 'utils';
import RenderMentions from 'components/SocialPost/RenderMentions';
import CommentForm from './CommentForm';
import {
	useCommentRepliesMutation,
	useDeleteCommentMutation,
	useLikeCommentMutation,
	useReportMutation,
} from 'services';
import toast from 'react-hot-toast';
import useAuthStore from 'store/auth';
import useCommonStore, { setModalConfig } from 'store/common';
import useTranslate from 'hooks/useTranslate';
import './styles.scss';
import { useNavigate } from 'react-router-dom';

interface FeedCommentsProps {
	comment: any;
	isThreaded?: boolean;
	setTotalNumberOfComments: any;
	removeComments?: any;
}

const FeedComments: React.FC<FeedCommentsProps> = ({
	comment,
	isThreaded = false,
	setTotalNumberOfComments,
	removeComments = () => {},
}) => {
	const {
		commentId,
		comment: content,
		createdAt,
		hashTags,
		profilePicture,
		taggedUsers,
		totalLikes,
		totalReplies,
		commentReply,
		isLike,
		userId,
		userType,
	} = comment;

	const navigate = useNavigate();
	const { translate } = useTranslate();
	const { userId: currentUserId }: any = useAuthStore((state) => state?.userInfo?.user);
	const { hideCommonModal }: any = useCommonStore((state) => state);
	const { mutateAsync: likeCommentMutation } = useLikeCommentMutation();
	const { mutateAsync: commentRepliesMutation } = useCommentRepliesMutation();
	const { mutateAsync: deleteCommentMutation } = useDeleteCommentMutation();
	const { mutateAsync: ReportMutation } = useReportMutation();

	const [isLiked, setIsLiked] = useState(isLike ? true : false);
	const [numberOfLikes, setNumberOfLikes] = useState(0);
	const [numberOfReplies, setNumberOfReplies] = useState<number>(0);
	const [repliesArr, setRepliesArr] = useState<string[]>(commentReply);
	const [pageForReply, setpageForReply] = useState<number>(0);
	const [replyMeta, setReplyMeta] = useState<any>({ hasNextPage: true });
	const [showCommentForm, setShowCommentForm] = useState<boolean>(false);

	useEffect(() => {
		setIsLiked(isLike ? true : false);
		setNumberOfLikes(Number(totalLikes || 0));
		setNumberOfReplies(Number(totalReplies || 0));
		setRepliesArr(commentReply);

		return () => {
			setShowCommentForm(false);
		};
	}, [comment?.commentId]);

	const onHandleLike = () => {
		likeCommentMutation({ commentId })
			.then((res: any) => {
				if (res?.success) {
					setIsLiked((prev) => {
						setNumberOfLikes((likes: any) => (!prev ? likes + 1 : Math.max(likes - 1, 0)));
						return !prev;
					});
				} else {
					toast.error(res?.message);
				}
			})
			.catch((error) => {
				console.error('Failed to like comment:', error);
				toast.error(
					translate('ToastMessage.titleFailed', { title: translate('Common.likeComment') }),
				);
			});
	};

	const onSeeMoreReplies = () => {
		setpageForReply((prev: number) => prev + 1);
	};

	useEffect(() => {
		if (pageForReply) {
			commentRepliesMutation({ id: commentId, pageForReply: pageForReply }).then((res: any) => {
				if (res?.success) {
					const { docs = [], meta = {} } = res?.data || {};
					setRepliesArr(pageForReply === 1 ? docs : [...repliesArr, ...docs]);
					setReplyMeta(meta);
				} else {
					toast.error(res?.message);
				}
			});
		}
	}, [pageForReply]);

	const onAddNewReply = (newData: any) => {
		setRepliesArr((prev: any) => [newData, ...prev]);
		setNumberOfReplies((prev: any) => prev + 1);
		setShowCommentForm(false);
	};

	const renderTooltip = (props: any) => (
		<Tooltip id="button-tooltip" {...props} className="d-none d-md-block">
			{currentUserId === userId
				? translate('Common.deleteComment')
				: translate('Common.reportComment')}
		</Tooltip>
	);

	const showReportModal = () => {
		setModalConfig({
			visible: true,
			id: Number(commentId),
			onClick: handleModalClick,
			type: 'reportFeeds',
			contentType: isThreaded ? 'socialPostCommentReply' : 'socialPostComment',
		});
	};

	const handleModalClick = (payload: any) => {
		ReportMutation(payload).finally(() => {
			hideCommonModal();
		});
	};

	const showDeleterModal = () => {
		setModalConfig({
			visible: true,
			type: 'confirmModal',
			onClick: deleteComment,
			data: {
				title: translate('Modal.wantToDelete', { title: `${translate('Modal.thisComment')}` }),
			},
			onClose: null,
			props: {
				dialogClassName: 'confirm-modal',
			},
		});
	};

	const removeRepliesArr = (commentId: number) => {
		setRepliesArr((prevReplies) =>
			prevReplies.filter((reply: any) => reply.commentId !== commentId),
		);
		if (!isThreaded) {
			setNumberOfReplies((prev) => Math.max(prev - 1, 0));
		}
		removeComments(commentId);
	};

	const deleteComment = () => {
		deleteCommentMutation(commentId)
			.then((_res: any) => {
				if (!isThreaded) {
					setNumberOfReplies((prev) => Math.max(prev - 1, 0));
				}
				removeComments(commentId);
				hideCommonModal();
			})
			.catch((error) => {
				console.error('Delete comment failed:', error);
				toast.error(
					translate('ToastMessage.titleFailed', { title: translate('Common.deleteComment') }),
				);
			});
	};

	const goToProfile = () => {
		navigate(`/${userType}/profile/${userId}`);
	};

	return (
		<div className="user-comment-box" key={commentId}>
			<div className="d-flex align-items-center justify-content-between">
				<div className="d-flex align-items-center gap-2">
					<Image
						onClick={goToProfile}
						style={{ cursor: 'pointer' }}
						src={profilePicture ? `${S3_URL}${profilePicture}` : IMAGE_PATHS.UserBG}
						className="comment-user-img object-fit-cover rounded-circle"
						loading="lazy"
					/>
					<h6
						onClick={goToProfile}
						style={{ cursor: 'pointer' }}
						className="comment-user-name fw-bold mb-0 ps-1"
					>
						{comment?.name}
					</h6>
					<p className="comment-user-time mb-0">{getCreatedDifferenceInBestUnit(createdAt)}</p>
				</div>
				<OverlayTrigger placement="left" delay={{ show: 250, hide: 400 }} overlay={renderTooltip}>
					{currentUserId !== userId ? (
						<Button onClick={showReportModal} className="info-btn border-0 p-0 bg-transparent">
							<i className="ri-information-line"></i>
						</Button>
					) : (
						<Button onClick={showDeleterModal} className="info-btn border-0 p-0 bg-transparent">
							<i className="ri-delete-bin-line"></i>
						</Button>
					)}
				</OverlayTrigger>
			</div>
			{content && (
				<div className="comment-msg">
					<p className="mb-0">
						<RenderMentions content={content} taggedUsers={taggedUsers} hashTags={hashTags} />
					</p>
				</div>
			)}
			<div className="d-flex gap-3">
				<Button className="like-comment border-0 p-0 bg-transparent" onClick={() => onHandleLike()}>
					<i className={`ri-thumb-up-${isLiked ? 'fill' : 'line'}`}></i>{' '}
					{unitNumber(numberOfLikes, 0)} {translate('Social.likes')}
				</Button>
				{!isThreaded && (
					<Button
						className="share-comment border-0 p-0 bg-transparent"
						onClick={() => setShowCommentForm(true)}
					>
						<i className="ri-share-forward-line"></i> {unitNumber(numberOfReplies, 0)}{' '}
						{translate('Social.replies')}
					</Button>
				)}
			</div>
			<Stack className="comment-replies gap-3">
				{!isThreaded && showCommentForm && (
					<CommentForm
						feedDetails={comment}
						setTotalNumberOfComments={setTotalNumberOfComments}
						setNumberOfReplies={setNumberOfReplies}
						onAddNewComment={onAddNewReply}
					/>
				)}
				<div className="comment-replies-inner">
					{repliesArr?.length > 0 &&
						repliesArr?.map((subComment: any, index: number) => (
							<div className="sub-comments-list" key={`sub-comments${index}`}>
								<FeedComments
									comment={subComment}
									isThreaded={true}
									setTotalNumberOfComments={setTotalNumberOfComments}
									removeComments={removeRepliesArr}
								/>
							</div>
						))}
				</div>
				{totalReplies > 0 && numberOfReplies > 1 && replyMeta?.hasNextPage && (
					<div onClick={() => onSeeMoreReplies()}>
						<Button className="view-reply-btn border-0 p-0 bg-transparent">
							{translate('Social.viewAllReplies')}
						</Button>
					</div>
				)}
			</Stack>
		</div>
	);
};

export default FeedComments;
